import type { RefObject } from 'react';
import { useCallback } from 'react';
import type { SurveyPage } from '../../fetchSurvey/types';
import type { SurveyAnswers } from '../../submitSurvey/types';
import type { BacktrackChangedAnswers } from '../../types/shared';
import { trackBacktrackRevisited } from '../telemetry/sessionTelemetry';
import type { SurveyPaginationRefs } from '../types';

export function useCheckBacktrackChanges(
  pages: SurveyPage[],
  values: SurveyAnswers,
  paginationRefs: RefObject<SurveyPaginationRefs>
) {
  return useCallback(
    (pageIndex: number) => {
      if (!paginationRefs.current) return;
      const backtrackInfo = paginationRefs.current.backtrackSnapshots[pageIndex];
      if (backtrackInfo) {
        const pageQuestions = pages[pageIndex]?.questions ?? [];
        const changedAnswers: BacktrackChangedAnswers = {};
        let hasChanges = false;
        pageQuestions.forEach(q => {
          const oldVal = backtrackInfo.snapshot[q.id];
          const newVal = values[q.id];
          if (oldVal !== newVal) {
            changedAnswers[q.id] = { oldValue: oldVal ?? null, newValue: newVal ?? null };
            hasChanges = true;
          }
        });
        if (hasChanges) {
          trackBacktrackRevisited(backtrackInfo.fromPageIndex, pageIndex, changedAnswers);
        }
        delete paginationRefs.current.backtrackSnapshots[pageIndex];
      }
    },
    [pages, values, paginationRefs]
  );
}
